export const LIST_WIDTH = 300;
const COLUMN_SIZES = ['s', 'm', 'l', 'xl'];
export function columnSizes() {
    return COLUMN_SIZES;
}
/**
 * localStorage key for the user settings of a data table.
 */
export function userSettingsKey(grid, userSettingsSuffix, name) {
    return 'paloma-ui.data-table.'
        + (grid.entity && grid.name != grid.entity.name ? (grid.name + '.') : '')
        + (userSettingsSuffix || name || Math.random() * 1000);
}
/**
 * column.name may contain periods, but a CSS identifier or slot name can't.
 */
export function cssName(columnName) {
    return columnName.replaceAll('.', '_');
}
export function createDefaultColumns(grid) {
    return grid.columns.map(col => ({ ...col, selected: col.visible, sorted: false }));
}
/**
 * Applies persisted visibility, sorting, order and size to the default columns.
 */
export function applyColumnUserSettings(columns, settings) {
    const withSettings = columns.map(col => {
        const setting = settings.find(s => s.name === col.name);
        if (!setting) {
            return col;
        }
        const restored = {
            ...col,
            selected: setting.selected,
            sorted: setting.sorted,
            size: setting.size || col.size,
        };
        if (setting.sortDirection !== undefined) {
            restored.sortDirection = setting.sortDirection;
        }
        return restored;
    });
    // reorder: columns with a persisted position first (at that position),
    //   columns without one after, in their default order
    const positioned = [];
    const remaining = [];
    withSettings.forEach(col => {
        const pos = settings.find(s => s.name === col.name)?.position;
        if (pos === undefined) {
            remaining.push(col);
        }
        else {
            positioned[pos] = col;
        }
    });
    // `positioned` may be sparse (e.g. 0, 1, 3) => forEach skips the gaps
    const reordered = [];
    positioned.forEach(col => reordered.push(col));
    remaining.forEach(col => reordered.push(col));
    return reordered;
}
export function toColumnUserSettings(columns) {
    return columns.map((col, position) => ({
        name: col.name,
        selected: col.selected,
        sorted: col.sorted,
        sortDirection: col.sortDirection,
        position: position,
        size: col.size,
    }));
}
/**
 * True if visibility, order or size of any column differs from the grid
 * defaults, i.e. if there is anything for resetColumns() to reset.
 */
export function areColumnsModified(columns, grid) {
    const defaults = grid.columns;
    if (columns.length !== defaults.length) {
        return true;
    }
    return columns.some((col, index) => col.name !== defaults[index].name
        || col.selected !== defaults[index].visible
        || col.size !== defaults[index].size);
}
/**
 * Resets visibility, order and size of the columns to the grid defaults.
 * The current sorting is kept, so the result set does not change.
 */
export function resetColumns(columns, grid) {
    const sortedColumn = columns.find(col => col.sorted);
    return createDefaultColumns(grid).map(col => {
        if (sortedColumn && col.name === sortedColumn.name) {
            return { ...col, sorted: true, sortDirection: sortedColumn.sortDirection };
        }
        return col;
    });
}
/**
 * 3 states:
 * unsorted => set sort asc
 * sorted asc => set sorted desc
 * sorted desc => set unsorted
 *
 * Sorting is exclusive: all other columns become unsorted.
 */
export function toggleColumnSort(columns, columnName) {
    return columns.map(col => {
        if (col.name === columnName) {
            if (!col.sorted) {
                return { ...col, sorted: true, sortDirection: 'asc' };
            }
            if (col.sortDirection === 'asc') {
                return { ...col, sortDirection: 'desc' };
            }
            return { ...col, sorted: false };
        }
        return col.sorted ? { ...col, sorted: false } : col;
    });
}
export function toggleColumnSelected(columns, columnName) {
    return columns.map(col => col.name === columnName ? { ...col, selected: !col.selected } : col);
}
export function resizeColumn(columns, columnName, size) {
    return columns.map(col => col.name === columnName ? { ...col, size } : col);
}
/**
 * Reorders the columns named in `order` accordingly; columns not named in
 * `order` (e.g. unselected ones) keep their relative order and follow after.
 */
export function reorderColumns(columns, order) {
    const ordered = [];
    const remaining = [];
    columns.forEach(col => {
        const pos = order.indexOf(col.name);
        if (pos === -1) {
            remaining.push(col);
        }
        else {
            ordered[pos] = col;
        }
    });
    const result = [];
    ordered.forEach(col => result.push(col));
    remaining.forEach(col => result.push(col));
    return result;
}
export function selectedColumnNames(columns) {
    return columns.filter(c => c.selected).map(c => c.name);
}
export function colWidth(size) {
    // NOTE: numbers as duplicated in styles
    switch (size) {
        case "s":
            return 100;
        case "m":
            return 200;
        case "l":
            return 400;
        case "xl":
            return 600;
    }
    return 200;
}
export function columnsWidth(columns) {
    return columns.reduce((width, col) => width + colWidth(col.size), 0);
}
export function hasStatusBadgeColumn(grid) {
    return grid.columns.some(col => col.name === 'status' && col.dataType === 'badge');
}
